import type { RouteLocationRaw } from 'vue-router';

import i18n from '@/i18n';

import type { InterviewData } from './entretienFactory';

import {
	type FollowingBaseInput,
	FollowingDropdownPopoverKind,
	FollowingDropdownTriggerMode,
	type FollowingLineGeneratorResult,
} from '@/components/following/types';

import {
	buildInterviewStatusDropdownSegment,
	EntretienType,
} from './entretienFactory';
import {
	createActionSegment,
	createLinkSegment,
	createStatusDropdownSegment,
	createStatusSegment,
	createTextSegment,
} from './segmentFactory';

export enum BesoinFollowingType {
	MOVED_TO_PRESELECTED = 'movedToPreselected',
	MOVED_TO_CONTACTED = 'movedToContacted',
	MOVED_TO_INTERVIEW_PLANNED = 'movedToInterviewPlanned',
	MOVED_TO_PROPOSED = 'movedToProposed',
	MOVED_TO_COMPANY_INTERVIEW = 'movedToCompanyInterview',
	MOVED_TO_MISSION_PROPOSED = 'movedToMissionProposed',
	MOVED_TO_POOL = 'movedToPool',
	REFUSED = 'refused',
}

interface BesoinStatusConfig {
	lineIcon: string;
	segmentIcon: string;
	color: string;
	labelKey: string;
	connectingKey: string;
}

const BESOIN_STATUS_CONFIGS: Record<BesoinFollowingType, BesoinStatusConfig> = {
	[BesoinFollowingType.MOVED_TO_PRESELECTED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--neutral-400, #9297AA)',
		labelKey: 'following.labels.besoin.statusPreselected',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_CONTACTED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--yellow-base, #FAB305)',
		labelKey: 'following.labels.besoin.statusContacted',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_INTERVIEW_PLANNED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--blue-base, #2051F8)',
		labelKey: 'following.labels.besoin.statusInterviewPlanned',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_PROPOSED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--blue-base, #2051F8)',
		labelKey: 'following.labels.besoin.statusProposed',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_COMPANY_INTERVIEW]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--purple-base, #7520E6)',
		labelKey: 'following.labels.besoin.statusCompanyInterview',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_MISSION_PROPOSED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--green-base, #00A355)',
		labelKey: 'following.labels.besoin.statusMissionProposed',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.MOVED_TO_POOL]: {
		lineIcon: 'folder-open',
		segmentIcon: 'star',
		color: 'var(--neutral-700, #323848)',
		labelKey: 'following.labels.besoin.statusPool',
		connectingKey: 'following.labels.besoin.ofNeed',
	},
	[BesoinFollowingType.REFUSED]: {
		lineIcon: 'folder-open',
		segmentIcon: 'status-marker',
		color: 'var(--red-base, #E51940)',
		labelKey: 'following.labels.besoin.statusRefused',
		connectingKey: 'following.labels.besoin.forNeed',
	},
};

const BESOIN_INTERVIEW_TYPES: Partial<
	Record<BesoinFollowingType, EntretienType>
> = {
	[BesoinFollowingType.MOVED_TO_INTERVIEW_PLANNED]: EntretienType.PLANNED,
	[BesoinFollowingType.MOVED_TO_COMPANY_INTERVIEW]: EntretienType.COMPANY,
};

const t = (key: string): string => i18n.global.t(key);

export interface BesoinFollowingInput extends FollowingBaseInput {
	type: BesoinFollowingType;
	besoinName?: string;
	besoinTo?: RouteLocationRaw;
	onBesoinClick?: (event: MouseEvent) => void;
	refusalReason?: string;
	interviewData?: InterviewData;
	comment?: string;
}

const buildBesoinNameSegment = (input: BesoinFollowingInput) => {
	if (!input.besoinName) return null;

	if (input.onBesoinClick) {
		return createActionSegment(input.besoinName, {
			onClick: input.onBesoinClick,
		});
	}

	if (input.besoinTo) {
		return createLinkSegment(input.besoinName, { to: input.besoinTo });
	}

	return createTextSegment(input.besoinName);
};

const buildStatusSegment = (
	input: BesoinFollowingInput,
	config: BesoinStatusConfig
) => {
	if (input.type === BesoinFollowingType.REFUSED && input.refusalReason) {
		return createStatusDropdownSegment(
			t(config.labelKey),
			config.color,
			{ type: FollowingDropdownPopoverKind.TEXT, payload: input.refusalReason },
			{ mode: FollowingDropdownTriggerMode.HOVER },
			config.segmentIcon
		);
	}

	const interviewType = BESOIN_INTERVIEW_TYPES[input.type];
	if (interviewType && input.interviewData) {
		return buildInterviewStatusDropdownSegment(
			t(config.labelKey),
			input.interviewData,
			interviewType,
			config.segmentIcon
		);
	}

	return createStatusSegment(
		t(config.labelKey),
		config.color,
		config.segmentIcon
	);
};

export const buildFollowingLineBesoin = (
	input: BesoinFollowingInput
): FollowingLineGeneratorResult => {
	const config = BESOIN_STATUS_CONFIGS[input.type];
	const besoinNameSegment = buildBesoinNameSegment(input);
	const statusSegment = buildStatusSegment(input, config);

	const trimmedComment = input.comment?.trim();

	return {
		icon: config.lineIcon,
		segments: [
			createTextSegment(t('following.labels.besoin.movedToward')),
			statusSegment,
			createTextSegment(t(config.connectingKey)),
			...(besoinNameSegment ? [besoinNameSegment] : []),
		],
		detail: input.detail,
		...(trimmedComment ? { content: trimmedComment } : {}),
	};
};
